import type { DesignTokens } from "../types/theme";

const WEB_SYSTEM_FONT =
  "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

// ── Journal — own identity (intimate, warm, serene) ────────────────────────────
// Light cream/linen ⇄ warm night ink, lavender accent and peach warmth.
// Kept in sync with packages/ui/vars.css (runtime source of truth).
const JOURNAL_INK = "#3b2f44"; // warm ink (text/brand)
const JOURNAL_INK_DARK = "#e8e3d9"; // texto sobre tinta nocturna
const JOURNAL_LAVENDER = "#8a6fb0"; // acento sereno (light)
const JOURNAL_LAVENDER_SOFT = "#a791c6"; // acento sereno (dark)
const JOURNAL_PEACH = "#e6a86b"; // calidez secundaria

/**
 * Base tokens for Light mode.
 */
export const LIGHT_BASE_TOKENS: DesignTokens = {
  background: "#f4f1ea",
  foreground: JOURNAL_INK,
  surface: "#fbf9f4",
  surfaceForeground: JOURNAL_INK,
  surfaceSecondary: "#efebe2",
  surfaceSecondaryForeground: JOURNAL_INK,
  surfaceTertiary: "#e6e0d4",
  surfaceTertiaryForeground: JOURNAL_INK,
  overlay: "#fbf9f4",
  overlayForeground: JOURNAL_INK,
  muted: "#7a7080",
  default: "#ece7dd",
  defaultForeground: JOURNAL_INK,
  accent: JOURNAL_LAVENDER,
  accentForeground: "#fbf9f4",
  danger: "#c4503f",
  dangerForeground: "#fbf9f4",
  warning: JOURNAL_PEACH,
  warningForeground: JOURNAL_INK,
  success: "#5b9c6f",
  successForeground: JOURNAL_INK,
  separator: "#cdc4b8",
  glassBackground: "rgba(251, 249, 244, 0.72)",
  glassBorder: "rgba(59, 47, 68, 0.1)",
  glassHighlight: "rgba(59, 47, 68, 0.07)",
  glow0: "rgba(138, 111, 176, 0.14)",
  glow1: "rgba(230, 168, 107, 0.12)",
  glow2: "rgba(91, 156, 111, 0.08)",
  mutedForeground: JOURNAL_INK,
  patternType: "none",
  patternOpacity: 10,
  surfaceShadow: "rgba(59, 47, 68, 0.12)",
  overlayShadow: "rgba(59, 47, 68, 0.18)",
  fieldShadow: "rgba(59, 47, 68, 0.06)",
  fieldBorder: "rgba(59, 47, 68, 0.12)",
  fieldPlaceholder: "rgba(59, 47, 68, 0.42)",
  fieldBackground: "rgba(59, 47, 68, 0.03)",
  segment: "#fbf9f4",
  segmentForeground: JOURNAL_INK,
  link: JOURNAL_LAVENDER,
  focus: JOURNAL_LAVENDER,
  fieldForeground: JOURNAL_INK,
  radius: "0.55rem",
  fontNormal: WEB_SYSTEM_FONT,
  fontMedium: WEB_SYSTEM_FONT,
  fontSemiBold: WEB_SYSTEM_FONT,
  fontBold: WEB_SYSTEM_FONT,
  border: "#cdc4b8",
  primary: JOURNAL_LAVENDER,
  primaryForeground: "#fbf9f4",
  secondary: "#ece7dd",
  secondaryForeground: JOURNAL_INK,
  backdrop: "rgba(40, 32, 48, 0.5)",
};

/**
 * Base tokens for Dark mode.
 */
export const DARK_BASE_TOKENS: DesignTokens = {
  background: "#211b27",
  foreground: JOURNAL_INK_DARK,
  surface: "#2a2330",
  surfaceForeground: JOURNAL_INK_DARK,
  surfaceSecondary: "#332b3a",
  surfaceSecondaryForeground: JOURNAL_INK_DARK,
  surfaceTertiary: "#3d3445",
  surfaceTertiaryForeground: JOURNAL_INK_DARK,
  overlay: "#2e2634",
  overlayForeground: JOURNAL_INK_DARK,
  muted: "#a79fb0",
  default: "#3d3445",
  defaultForeground: JOURNAL_INK_DARK,
  accent: JOURNAL_LAVENDER_SOFT,
  accentForeground: "#241d2a",
  danger: "#d96a5a",
  dangerForeground: "#fbf9f4",
  warning: "#e6b478",
  warningForeground: "#241d2a",
  success: "#76b489",
  successForeground: "#241d2a",
  separator: "#4a4153",
  glassBackground: "rgba(42, 35, 48, 0.62)",
  glassBorder: "rgba(232, 227, 217, 0.12)",
  glassHighlight: "rgba(232, 227, 217, 0.22)",
  glow0: "rgba(167, 145, 198, 0.42)",
  glow1: "rgba(230, 168, 107, 0.34)",
  glow2: "rgba(118, 180, 137, 0.26)",
  mutedForeground: JOURNAL_INK_DARK,
  patternType: "none",
  patternOpacity: 10,
  surfaceShadow: "rgba(0, 0, 0, 0.4)",
  overlayShadow: "rgba(0, 0, 0, 0.55)",
  fieldShadow: "rgba(0, 0, 0, 0.3)",
  fieldBorder: "rgba(232, 227, 217, 0.12)",
  fieldPlaceholder: "rgba(232, 227, 217, 0.42)",
  fieldBackground: "rgba(232, 227, 217, 0.04)",
  segment: "#4a4153",
  segmentForeground: JOURNAL_INK_DARK,
  link: JOURNAL_LAVENDER_SOFT,
  focus: JOURNAL_LAVENDER_SOFT,
  fieldForeground: JOURNAL_INK_DARK,
  radius: "0.55rem",
  fontNormal: WEB_SYSTEM_FONT,
  fontMedium: WEB_SYSTEM_FONT,
  fontSemiBold: WEB_SYSTEM_FONT,
  fontBold: WEB_SYSTEM_FONT,
  border: "#4a4153",
  primary: JOURNAL_LAVENDER_SOFT,
  primaryForeground: "#241d2a",
  secondary: "#3d3445",
  secondaryForeground: JOURNAL_INK_DARK,
  backdrop: "rgba(0, 0, 0, 0.7)",
};
